/** Shared tournament form helpers (web organizer form + public enrollment). */

export const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export const BALL_TYPE_OPTIONS = [
  { value: 'tennis', label: 'Tennis ball' },
  { value: 'leather', label: 'Leather ball' },
  { value: 'other', label: 'Other' },
];

export const FEE_OPTIONS = [500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 5000];

export function todayYmdLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseDateOnly(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  return m ? m[1] : null;
}

function addDaysYmd(ymd, days) {
  const [y, mo, d] = ymd.split('-').map(Number);
  const dt = new Date(y, mo - 1, d + days);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function weekdayFromYmd(ymd) {
  const [y, mo, d] = ymd.split('-').map(Number);
  return new Date(y, mo - 1, d).getDay();
}

export function listMatchDayOccurrences(tournamentStartsAt, tournamentEndsAt, matchDayOfWeek) {
  const start = parseDateOnly(tournamentStartsAt);
  const end = parseDateOnly(tournamentEndsAt);
  if (!start || !end || matchDayOfWeek === '' || matchDayOfWeek == null) return [];
  if (end < start) return [];

  const dow = Number(matchDayOfWeek);
  if (!Number.isInteger(dow) || dow < 0 || dow > 6) return [];

  const dates = [];
  let cur = start;
  while (cur <= end) {
    if (weekdayFromYmd(cur) === dow) dates.push(cur);
    cur = addDaysYmd(cur, 1);
  }
  return dates;
}

export function requiredAvailabilityCount(matchesPerTeam) {
  const n = Number(matchesPerTeam);
  if (!Number.isInteger(n) || n < 1) return 0;
  return Math.ceil(1.5 * n);
}

export function formatYmdShort(ymd) {
  if (!ymd) return '';
  const [y, mo, d] = ymd.split('-').map(Number);
  return new Date(y, mo - 1, d).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
  });
}

export function formatRupee(value) {
  if (value == null || value === '') return null;
  return `₹${Number(value).toLocaleString('en-IN')}`;
}

/** Enrollment fee rows — show an em dash when the organizer left a field unset. */
export function formatFeeLine(value) {
  const formatted = formatRupee(value);
  return formatted ?? '—';
}

/** True when a fee field was set by the organizer (including zero). */
export function hasConfiguredFee(value) {
  return value != null && value !== '';
}

export function signupPaymentTotal(registrationAmount, securityAmount) {
  const reg = registrationAmount != null && registrationAmount !== '' ? Number(registrationAmount) : 0;
  const sec = securityAmount != null && securityAmount !== '' ? Number(securityAmount) : 0;
  const total = reg + sec;
  return total > 0 ? total : null;
}

export function emptyTournamentForm() {
  return {
    name: '',
    description: '',
    format_details: '',
    rules_details: '',
    prizes_details: '',
    registration_starts_at: todayYmdLocal(),
    registration_ends_at: '',
    tournament_starts_at: '',
    tournament_ends_at: '',
    max_teams: '',
    registration_fee: '',
    registration_fee_afternoon: '',
    registration_amount: '',
    security_amount: '',
    ball_type: 'tennis',
    overs_per_match: '8',
    matches_per_team: '6',
    match_day_of_week: '6',
    playoff_qualifiers: '',
    upi_id: '',
    is_published: false,
  };
}

export function tournamentToForm(t) {
  if (!t) return emptyTournamentForm();
  const toDateInput = (d) => (d ? String(d).slice(0, 10) : '');
  return {
    name: t.name || '',
    description: t.description || '',
    format_details: t.format_details || '',
    rules_details: t.rules_details || '',
    prizes_details: t.prizes_details || '',
    registration_starts_at: toDateInput(t.registration_starts_at) || todayYmdLocal(),
    registration_ends_at: toDateInput(t.registration_ends_at),
    tournament_starts_at: toDateInput(t.tournament_starts_at),
    tournament_ends_at: toDateInput(t.tournament_ends_at),
    max_teams: t.max_teams != null ? String(t.max_teams) : '',
    registration_fee: t.registration_fee != null ? String(t.registration_fee) : '',
    registration_fee_afternoon:
      t.registration_fee_afternoon != null ? String(t.registration_fee_afternoon) : '',
    registration_amount: t.registration_amount != null ? String(t.registration_amount) : '',
    security_amount: t.security_amount != null ? String(t.security_amount) : '',
    ball_type: t.ball_type || 'tennis',
    overs_per_match: t.overs_per_match != null ? String(t.overs_per_match) : '',
    matches_per_team: t.matches_per_team != null ? String(t.matches_per_team) : '',
    match_day_of_week:
      t.match_day_of_week != null ? String(t.match_day_of_week) : '6',
    playoff_qualifiers: t.playoff_qualifiers != null ? String(t.playoff_qualifiers) : '',
    upi_id: t.upi_id || '',
    is_published: Boolean(t.is_published),
  };
}

export function formToApiBody(form, { publish = false } = {}) {
  return {
    name: form.name.trim(),
    description: form.description.trim() || null,
    format_details: form.format_details.trim() || null,
    rules_details: form.rules_details.trim() || null,
    prizes_details: form.prizes_details.trim() || null,
    registration_starts_at: form.registration_starts_at
      ? `${form.registration_starts_at}T00:00:00`
      : null,
    registration_ends_at: form.registration_ends_at
      ? `${form.registration_ends_at}T23:59:59`
      : null,
    tournament_starts_at: form.tournament_starts_at || null,
    tournament_ends_at: form.tournament_ends_at || null,
    max_teams: form.max_teams === '' ? null : form.max_teams,
    registration_fee: form.registration_fee === '' ? null : form.registration_fee,
    registration_fee_afternoon:
      form.registration_fee_afternoon === '' ? null : form.registration_fee_afternoon,
    registration_amount: form.registration_amount === '' ? null : form.registration_amount,
    security_amount: form.security_amount === '' ? null : form.security_amount,
    ball_type: form.ball_type || null,
    overs_per_match: form.overs_per_match === '' ? null : form.overs_per_match,
    matches_per_team: form.matches_per_team === '' ? null : form.matches_per_team,
    match_day_of_week: form.match_day_of_week === '' ? null : form.match_day_of_week,
    playoff_qualifiers: form.playoff_qualifiers === '' ? null : form.playoff_qualifiers,
    upi_id: form.upi_id.trim() || null,
    is_published: publish,
  };
}
